import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from '@store/hooks'
import {
  fetchTreatmentPlanbyPatientId,
  updateTreatmentPlan,
} from '@store/actions'
import { Tab, Nav, Row, Col, Accordion, Spinner } from 'react-bootstrap'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import { ClipMode } from '@constants/Common/GridComponent.contants'
import TreatmentPlanToothChart from './TreatmentPlanToothChart/TreatmentPlanToothChart.component'
import TxPlanStatusDropdown from './TxPlanStatusDropdown'
import { getFullName } from '@utils/fullname.util'
import {
  ITreatmentPlan,
  TxPlanStatus,
  IPhaseItems,
  IPhases,
  TX_PLAN_STATUS,
} from '@dataTypes/treatmentPlan.types'
import { formatNumber } from '@utils/numberFormatter.util'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
} from '@utils/treatment-plan.utils'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'
import Button from '@components/common/Button/Button.component'
import EmptyTreatmentPlan from './EmptyState/EmptyTreatmentPlan.component'
import { Patient } from '@dataTypes/PatientInfo.types'
import PhaseActionMenuButton from './PhaseActionMenuButton/PhaseActionMenuButton.component'
import { MenuEventArgs, ItemModel } from '@syncfusion/ej2-react-splitbuttons'
import './TreatmentPlanMain.css'
import {
  getTreatmentPlanChartAccordionClassName,
  TP_CHART_PANE_MIN_PX,
  TP_CHART_VERTICAL_HANDLE_PX,
  useTreatmentPlanChartVerticalSplit,
} from './useTreatmentPlanChartVerticalSplit.hook'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import { filterTxPlanInsuranceEstimateColumns } from '@utils/tx-plan-procedure-grid-columns.util'

interface TreatmentPlanHistoryProps {
  onViewNote?: (id: string) => void
  onGoToPlans?: () => void
  patient?: Partial<Patient> | null
}

const TreatmentPlanHistory: React.FC<TreatmentPlanHistoryProps> = ({
  onViewNote,
  onGoToPlans,
  patient: patientProp,
}): JSX.Element => {
  const selectedPatientFromRedux = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const allTreatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const { showInsuranceEstimate } = useClinicChartingPreferences()
  const providerList = useAppSelector((state) => state.provider.data)
  const dispatch = useAppDispatch()

  // Use patient prop if provided, otherwise fall back to Redux
  const selectedPatient = patientProp || selectedPatientFromRedux

  // Fetch treatment plans when patient prop is provided
  useEffect(() => {
    if (patientProp?.patient_id) {
      dispatch(fetchTreatmentPlanbyPatientId(patientProp.patient_id))
    }
  }, [patientProp?.patient_id, dispatch])

  // Filter treatment plans by patient if patient prop is provided
  const treatmentPlans = useMemo(() => {
    if (patientProp?.patient_id) {
      return allTreatmentPlans.filter(
        (plan) => plan.patient_id === patientProp.patient_id,
      )
    }
    return allTreatmentPlans
  }, [allTreatmentPlans, patientProp])

  const [activeTabKey, setActiveTabKey] = useState<string>('')
  const [chartVisible, setChartVisible] = useState<boolean>(true)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const [phaseVisibility, setPhaseVisibility] = useState<
    Record<string, boolean>
  >({})

  const completedTreatmentPlans = useMemo(() => {
    return treatmentPlans.filter((plan) => {
      if (!plan.phases || plan.phases.length === 0) return false
      return plan.phases.every(
        (phase) => phase.status === TX_PLAN_STATUS.COMPLETED,
      )
    })
  }, [treatmentPlans])

  const isEmptyState = completedTreatmentPlans.length === 0
  const {
    chartPaneHeightPx,
    chartPaneRef,
    onHandleMouseDown: onChartVerticalHandleMouseDown,
  } = useTreatmentPlanChartVerticalSplit({
    isSplitActive: chartVisible,
    containerRef: mainContainerRef,
    isEmptyState,
  })

  // Set active tab to first completed plan when available
  useEffect(() => {
    if (completedTreatmentPlans.length > 0 && !activeTabKey) {
      setActiveTabKey(completedTreatmentPlans[0].tp_id)
    } else if (completedTreatmentPlans.length === 0) {
      setActiveTabKey('')
    }
  }, [completedTreatmentPlans, activeTabKey])

  // Toggle phase visibility in chart
  const togglePhaseVisibility = useCallback((phaseId: string): void => {
    setPhaseVisibility((prev) => ({
      ...prev,
      [phaseId]: !(prev[phaseId] ?? true),
    }))
  }, [])

  // Get phase visibility state (defaults to true)
  const getPhaseVisibility = (phaseId: string): boolean => {
    return phaseVisibility[phaseId] ?? true
  }

  // Handle marking phase as incomplete (changes status to Accepted)
  const handleMarkPhaseAsIncomplete = useCallback(
    async (phaseId: string, planId: string): Promise<void> => {
      const plan = completedTreatmentPlans.find((p) => p.tp_id === planId)
      if (!plan || !plan.phases) return

      const updatedPhases = plan.phases.map((phase) =>
        phase.phase_id === phaseId
          ? { ...phase, status: TX_PLAN_STATUS.ACCEPTED }
          : phase,
      )

      await dispatch(
        updateTreatmentPlan({
          treatmentPlanId: planId,
          treatmentPlan: { ...plan, phases: updatedPhases },
        }),
      )
    },
    [completedTreatmentPlans, dispatch],
  )

  // Handle menu selection from PhaseActionMenuButton
  const handleMenuSelect = useCallback(
    (
      args: MenuEventArgs,
      tabKey: string,
      originalIdx: number,
      phase: IPhases,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _plan: ITreatmentPlan | null,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _isEditMode: boolean,
    ): void => {
      const itemId = args.item?.id

      if (itemId === 'mark-as-incomplete') {
        const phaseId = phase.phase_id || `phase-${originalIdx}`
        handleMarkPhaseAsIncomplete(phaseId, tabKey)
      }
    },
    [handleMarkPhaseAsIncomplete],
  )

  // Create custom menu items for history tab (only "Mark as incomplete")
  const historyMenuItems: ItemModel[] = [
    {
      text: 'Mark as incomplete',
      id: 'mark-as-incomplete',
    },
  ]

  const handleTabSelect = (key: string | null): void => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  // Get chart header name
  const chartHeaderName = useMemo(() => {
    if (!activeTabKey) return 'Current chart'
    const plan = completedTreatmentPlans.find((p) => p.tp_id === activeTabKey)
    if (plan) {
      return `${plan.tp_heading || 'Treatment plan'} - Chart`
    }
    return 'Current chart'
  }, [completedTreatmentPlans, activeTabKey])

  // Memoized form state phases for chart (read-only, from Redux)
  const memoizedFormStatePhases = useMemo(() => {
    if (!activeTabKey) return null
    const plan = completedTreatmentPlans.find((p) => p.tp_id === activeTabKey)
    return plan?.phases || null
  }, [completedTreatmentPlans, activeTabKey])

  const renderTreatmentPlanForm = (
    tabKey: string,
    plan: ITreatmentPlan,
  ): JSX.Element => {
    const phases = plan.phases || []

    const totals: {
      charges: number
      insurance: number
      adjustments: number
      outstanding: number
    } = phases.reduce(
      (sum, phase) => {
        for (const item of phase.items) {
          sum.charges += item.charges
          sum.insurance += item.insurance
          sum.adjustments += item.adjustments
          sum.outstanding += item.outstanding
        }
        return sum
      },
      { charges: 0, insurance: 0, adjustments: 0, outstanding: 0 },
    )

    return (
      <div className="treatment-plan-form-container">
        <div className="create-tx-plan-container">
          <Row className="align-items-center gx-0">
            <Col className="d-flex align-items-end flex-nowrap gap-1 create-tx-plan-phase-header"></Col>
            <Col
              md="12"
              className="d-flex justify-content-end align-items-center ctx-custom-buttons"
            ></Col>
          </Row>

          {/* Phases */}
          <Accordion
            id={`tx-plan-phase-accordion-${tabKey}`}
            className="tx-plan-phase-accordion"
            defaultActiveKey={[]}
            alwaysOpen
          >
            {phases
              .map((phase, originalIdx) => ({ phase, originalIdx }))
              .filter(({ phase }) => !phase.is_hidden)
              .map(({ phase, originalIdx }, phaseIdx) => {
                const isFirstTab =
                  completedTreatmentPlans.length > 0
                    ? tabKey === completedTreatmentPlans[0]?.tp_id
                    : false
                const isFirstPhase = phaseIdx === 0
                const isFirstPhaseInFirstTab = isFirstTab && isFirstPhase

                // Read-only tooth surface template
                const toothSurfaceTemplate = (
                  props: { surface?: string | null } & IPhaseItems,
                ): JSX.Element => {
                  const toothSurfaceEntries = getTSFromToothSurfaces(
                    props.tooth_surfaces,
                  )
                  const surfaceEntries = parseSurfaceString(props.surface)
                  const parsedEntries =
                    toothSurfaceEntries.length > 0
                      ? toothSurfaceEntries
                      : surfaceEntries

                  if (parsedEntries.length === 0) return <span>-</span>

                  return (
                    <div className="template-cell-tooth-surface">
                      {formatToothSurfaceEntriesForDisplay(parsedEntries)}
                    </div>
                  )
                }

                const phaseColumns: ColumnModel[] = [
                  {
                    field: 'code',
                    headerText: 'Code',
                    allowSorting: true,
                    width: '8%',
                  },
                  {
                    field: 'description',
                    headerText: 'Description',
                    allowSorting: true,
                    width: '30%',
                  },
                  {
                    field: 'provider',
                    headerText: 'Provider',
                    width: '12%',
                    template: (props: IPhaseItems & { provider?: string }) => {
                      const provider = providerList.find(
                        (p) => p.provider_id === props.provider_id,
                      )
                      return provider ? `Dr. ${getFullName(provider)}` : '-'
                    },
                  },
                  {
                    field: 'surface',
                    headerText: 'T/S',
                    width: '8%',
                    template: toothSurfaceTemplate,
                  },
                  {
                    field: 'charges',
                    headerText: 'Clinic fee',
                    allowSorting: true,
                    width: '7%',
                    template: (props: IPhaseItems & { charges?: number }) => {
                      return formatNumber(props.charges ?? 0, true)
                    },
                  },
                  {
                    field: 'allowed_amt',
                    headerText: 'Allowed amt.',
                    clipMode: ClipMode.EllipsisWithTooltip,
                    width: '7%',
                    template: (
                      props: IPhaseItems & { allowed_amount?: number | null },
                    ) => (
                      <span>
                        {formatNumber(props.allowed_amount ?? 0, true)}
                      </span>
                    ),
                  },
                  {
                    field: 'write_off',
                    headerText: 'Write-off',
                    clipMode: ClipMode.EllipsisWithTooltip,
                    width: '7%',
                    template: (
                      props: IPhaseItems & { write_off?: number | null },
                    ) => (
                      <span>{formatNumber(props.write_off ?? 0, true)}</span>
                    ),
                  },
                  {
                    field: 'adjustments',
                    headerText: 'Discount',
                    clipMode: ClipMode.EllipsisWithTooltip,
                    width: '7%',
                    template: (
                      props: IPhaseItems & { adjustments?: number },
                    ) => (
                      <span>{formatNumber(props.adjustments ?? 0, true)}</span>
                    ),
                  },
                  {
                    field: 'insurance',
                    headerText: 'Est. ins. pay',
                    clipMode: ClipMode.EllipsisWithTooltip,
                    width: '7%',
                    template: (props: IPhaseItems & { insurance?: number }) => (
                      <span>{formatNumber(props.insurance ?? 0, true)}</span>
                    ),
                  },
                  {
                    field: 'outstanding',
                    headerText: 'Est. pt. res.',
                    clipMode: ClipMode.EllipsisWithTooltip,
                    width: '7%',
                    template: (
                      props: IPhaseItems & { outstanding?: number },
                    ) => (
                      <span>{formatNumber(props.outstanding ?? 0, true)}</span>
                    ),
                  },
                ]
                const visiblePhaseColumns =
                  filterTxPlanInsuranceEstimateColumns(
                    phaseColumns,
                    showInsuranceEstimate,
                  )

                return (
                  <Accordion.Item
                    eventKey={phase.phase_id || `phase-${originalIdx}`}
                    key={phase.phase_id || `phase-${originalIdx}`}
                    className="rounded"
                  >
                    <Accordion.Header
                      as="div"
                      className={`tx-plan-phase-header border-0${
                        isFirstPhaseInFirstTab
                          ? ' first-phase-header-no-radius'
                          : ''
                      }`}
                    >
                      <div className="w-100">
                        <Row className="align-items-center">
                          <Col className="d-flex flex-row align-items-center gap-2 flex-wrap">
                            <div className="create-tx-plan-set-date d-flex flex-row align-items-center gap-2 flex-wrap">
                              <div className="create-tx-plan-phase-name">
                                {phase.phase_name}
                              </div>
                              {phase.scheduled_date && (
                                <span>
                                  Scheduled{' '}
                                  {new Date(
                                    phase.scheduled_date,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                              {!phase.scheduled_date && (
                                <span>Unscheduled</span>
                              )}
                            </div>
                          </Col>
                          <Col className="d-flex flex-row gap-2 justify-content-end">
                            <div
                              className="d-flex flex-row gap-1 align-items-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i
                                className={`phase-visibility-button ti ${
                                  getPhaseVisibility(
                                    phase.phase_id || `phase-${originalIdx}`,
                                  )
                                    ? 'mgc_eye_2_fill'
                                    : 'ti-eye-off'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  togglePhaseVisibility(
                                    phase.phase_id || `phase-${originalIdx}`,
                                  )
                                }}
                                title={
                                  getPhaseVisibility(
                                    phase.phase_id || `phase-${originalIdx}`,
                                  )
                                    ? 'Hide phase conditions in chart'
                                    : 'Show phase conditions in chart'
                                }
                              />
                              <TxPlanStatusDropdown
                                value={
                                  (phase.status ||
                                    TX_PLAN_STATUS.PROPOSED) as TxPlanStatus
                                }
                                readOnly
                              />
                              <PhaseActionMenuButton
                                tabKey={tabKey}
                                originalIdx={originalIdx}
                                phase={phase}
                                plan={plan}
                                isEditMode={false}
                                onMenuSelect={handleMenuSelect}
                                customMenuItems={historyMenuItems}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="create-tx-plan-phase-table">
                        {phase.items.filter((item) => !item.is_hidden).length >
                          0 && (
                          <DataGridComponent
                            columns={visiblePhaseColumns}
                            data={phase.items
                              .map((p) => ({
                                ...p,
                                code: p.procedure_settings?.procedure
                                  ?.procedure_code,
                                description:
                                  p.procedure_settings?.procedure?.description,
                              }))
                              .filter((item) => !item.is_hidden)}
                            allowPaging={false}
                            allowSorting={true}
                          />
                        )}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                )
              })}
          </Accordion>
        </div>

        {/* Totals */}
        <div className="create-tx-plan-summary">
          <div className="ctps-title">Plan pricing summary</div>
          <Row>
            <Col xs={6}>
              <div className="tx-label">Total charges:</div>
              {showInsuranceEstimate && (
                <div className="tx-label">Insurance covered:</div>
              )}
              <div className="tx-label">Adjustments:</div>
            </Col>
            <Col xs={6} className="text-end">
              <div className="tx-amount">
                {formatNumber(totals.charges, true)}
              </div>
              {showInsuranceEstimate && (
                <div className="tx-amount">
                  {formatNumber(totals.insurance, true)}
                </div>
              )}
              <div className="tx-amount">
                {formatNumber(totals.adjustments, true)}
              </div>
            </Col>
          </Row>
          {showInsuranceEstimate && (
            <Row className="border-top pt-2 mt-2">
              <Col xs={6} className="tx-label">
                Adjusted amount:
              </Col>
              <Col xs={6} className="text-end tx-amount">
                {formatNumber(totals.outstanding, true)}
              </Col>
            </Row>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={mainContainerRef}
      className={`treatment-plan-main-container ${chartingLoading ? 'overlay-active' : ''}`}
    >
      {chartingLoading && (
        <div className="treatment-plan-overlay">
          <div className="treatment-plan-loading">
            <Spinner
              animation="border"
              variant="primary"
              role="status"
              className="treatment-plan-loader"
            />
            <p className="treatment-plan-loading-text">
              Loading patient treatment plans...
            </p>
          </div>
        </div>
      )}

      {!selectedPatient ? (
        <EmptyTreatmentPlan description="Select a patient to view their treatment plans." />
      ) : (
        <>
          <Accordion
            activeKey={chartVisible ? 'chart' : ''}
            onSelect={() => {}}
            className="mb-0 treatment-plan-chart-accordion-sticky"
          >
            <Accordion.Item
              eventKey="chart"
              className={getTreatmentPlanChartAccordionClassName(chartVisible)}
            >
              <Accordion.Header
                as="div"
                className="treatment-plan-chart-header p-0"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <div className="treatment-plan-chart-header-content">
                  <div className="d-flex align-items-center gap-2">
                    <span className="treatment-plan-chart-header-title">
                      {chartHeaderName}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className="small">Dental chart</span>
                      <i
                        className={`icon-wrap ${chartVisible ? 'mgc_eye_2_fill' : 'mgc_eye_close_line'}`}
                        style={{ cursor: 'pointer', fontSize: '18px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setChartVisible(!chartVisible)
                        }}
                      ></i>
                    </div>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div
                  ref={chartPaneRef}
                  className={`treatment-plan-chart-body-content${
                    chartPaneHeightPx !== null
                      ? ' treatment-plan-chart-body-content--sized'
                      : ''
                  }`}
                  style={
                    chartPaneHeightPx !== null
                      ? ({
                          '--tp-chart-pane-height': `${chartPaneHeightPx}px`,
                          height: chartPaneHeightPx,
                          maxHeight: chartPaneHeightPx,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <TreatmentPlanToothChart
                    activeTreatmentPlanId={
                      activeTabKey && activeTabKey !== 'create-new'
                        ? activeTabKey
                        : null
                    }
                    phaseVisibility={phaseVisibility}
                    formStatePhases={memoizedFormStatePhases}
                    fillPane={chartPaneHeightPx !== null}
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          {chartVisible ? (
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize dental chart"
              aria-valuenow={chartPaneHeightPx ?? TP_CHART_PANE_MIN_PX}
              aria-valuemin={TP_CHART_PANE_MIN_PX}
              tabIndex={0}
              className="treatment-plan-chart-vertical-handle"
              onMouseDown={onChartVerticalHandleMouseDown}
              style={{ flex: `0 0 ${TP_CHART_VERTICAL_HANDLE_PX}px` }}
            >
              <i
                className="mgc_unfold_vertical_line treatment-plan-chart-vertical-handle__icon"
                aria-hidden
              />
            </div>
          ) : null}
          {completedTreatmentPlans.length === 0 ? (
            <div className="treatment-plan-empty-scroll">
              <EmptyTreatmentPlan
                title="No completed plan"
                description="Completed treatment plans will be added here."
                actionLabel="Go to plan"
                onCreateNewPlan={onGoToPlans}
              />
            </div>
          ) : (
            <div className="treatment-plan-tabs-container">
              <Tab.Container
                activeKey={activeTabKey}
                onSelect={handleTabSelect}
              >
                <Nav variant="tabs" className="treatment-plan-tabs">
                  {completedTreatmentPlans.map((plan) => (
                    <Nav.Item key={plan.tp_id}>
                      <Nav.Link eventKey={plan.tp_id}>
                        <span className="d-flex align-items-center gap-2">
                          {plan.tp_heading || 'Untitled Plan'}
                        </span>
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                  {activeTabKey &&
                    completedTreatmentPlans.find(
                      (p) => p.tp_id === activeTabKey,
                    ) &&
                    onViewNote && (
                      <Nav.Item className="ms-auto treatment-plan-tab-actions">
                        <div className="d-flex align-items-center gap-1">
                          <Button
                            size="sm"
                            className="icon-wrap"
                            title="Add note"
                            onClick={() => {
                              const plan = completedTreatmentPlans.find(
                                (p) => p.tp_id === activeTabKey,
                              )
                              if (plan) {
                                onViewNote(plan.tp_id)
                              }
                            }}
                          >
                            <i className="mgc_document_fill" />
                          </Button>
                        </div>
                      </Nav.Item>
                    )}
                </Nav>
                <Tab.Content>
                  {completedTreatmentPlans.map((plan) => (
                    <Tab.Pane eventKey={plan.tp_id} key={plan.tp_id}>
                      {activeTabKey === plan.tp_id
                        ? renderTreatmentPlanForm(plan.tp_id, plan)
                        : null}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Tab.Container>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default TreatmentPlanHistory
